// ===================================
// TODO CLEAN - QUOTE REQUEST WIZARD
// Multi-step form to collect client information for quotation
// ===================================

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SITE_CONFIG } from '@/utils/constants';
import {
  HomeIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  HomeModernIcon,
  BuildingStorefrontIcon,
  MapPinIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { WhatsAppIcon } from '@/components/icons/SocialIcons';

// ==========================================
// INTERFACES
// ==========================================

interface FormState {
  propertyType: string;
  squareMeters: string;
  rooms: string;
  bathrooms: string;
  frequency: string;
  location: string;
  name: string;
  additionalInfo: string;
}

interface PriceCalculatorProps {
  onSuccess?: () => void;
}

// ==========================================
// COMPONENT DATA
// ==========================================

const PROPERTY_OPTIONS = [
  { id: 'casa', name: 'Casa', icon: HomeIcon, color: 'from-cyan-500 to-teal-500' },
  { id: 'departamento', name: 'Departamento', icon: BuildingOfficeIcon, color: 'from-blue-500 to-indigo-500' },
  { id: 'oficina', name: 'Oficina', icon: BriefcaseIcon, color: 'from-purple-500 to-pink-500' },
  { id: 'airbnb', name: 'Airbnb', icon: HomeModernIcon, color: 'from-orange-500 to-red-500' },
  { id: 'local', name: 'Local', icon: BuildingStorefrontIcon, color: 'from-emerald-500 to-green-500' },
  { id: 'otro', name: 'Otro', icon: MapPinIcon, color: 'from-gray-500 to-slate-500' },
];

const SQUARE_METER_OPTIONS = [
  { id: 'small', label: 'Menos de 50m²', description: 'Espacio pequeño' },
  { id: 'medium', label: '50 - 100m²', description: 'Espacio mediano' },
  { id: 'large', label: '100 - 150m²', description: 'Espacio grande' },
  { id: 'xlarge', label: 'Más de 150m²', description: 'Espacio muy grande' },
];

const ROOM_OPTIONS = ['1', '2', '3', '4', '5+'];
const BATHROOM_OPTIONS = ['1', '2', '3', '4+'];

const FREQUENCY_OPTIONS = [
  { id: 'unica', label: 'Una vez', description: 'Sin compromiso', color: 'from-cyan-500 to-teal-500' },
  { id: 'semanal', label: 'Semanal', description: '10% descuento', color: 'from-green-500 to-emerald-500' },
  { id: 'quincenal', label: 'Quincenal', description: '5% descuento', color: 'from-blue-500 to-indigo-500' },
  { id: 'mensual', label: 'Mensual', description: 'Precio normal', color: 'from-purple-500 to-pink-500' },
];

const LOCATION_OPTIONS = [
  'Chillán Centro', 'Chillán Viejo', 'Quilamapu', 'Vicente Méndez', 'Las Mariposas', 'Otro sector'
];

const STEPS = [
  { id: 1, title: 'Tipo de espacio', icon: HomeIcon },
  { id: 2, title: 'Tamaño', icon: MapPinIcon },
  { id: 3, title: 'Detalles', icon: BuildingOfficeIcon },
  { id: 4, title: 'Confirmar', icon: CheckIcon },
];

// ==========================================
// MAIN COMPONENT
// ==========================================

const PriceCalculator: React.FC<PriceCalculatorProps> = ({ onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formState, setFormState] = useState<FormState>({
    propertyType: '',
    squareMeters: '',
    rooms: '',
    bathrooms: '',
    frequency: '',
    location: '',
    name: '',
    additionalInfo: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: keyof FormState, value: string) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return !!formState.propertyType;
      case 2: return !!formState.squareMeters;
      case 3: return !!formState.rooms && !!formState.bathrooms && !!formState.frequency && !!formState.location;
      case 4: return true;
      default: return false;
    }
  };

  const nextStep = () => {
    if (canProceed() && currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const generateWhatsAppMessage = () => {
    const propertyName = PROPERTY_OPTIONS.find(p => p.id === formState.propertyType)?.name || formState.propertyType;
    const squareMetersLabel = SQUARE_METER_OPTIONS.find(s => s.id === formState.squareMeters)?.label || formState.squareMeters;
    const frequencyLabel = FREQUENCY_OPTIONS.find(f => f.id === formState.frequency)?.label || formState.frequency;

    let message = `¡Hola! Me gustaría solicitar una cotización:

*DATOS DE LA PROPIEDAD*
• Tipo: ${propertyName}
• Tamaño: ${squareMetersLabel}
• Habitaciones: ${formState.rooms}
• Baños: ${formState.bathrooms}
• Frecuencia: ${frequencyLabel}
• Ubicación: ${formState.location}`;

    if (formState.name) {
      message += `\n\n*NOMBRE*: ${formState.name}`;
    }

    if (formState.additionalInfo) {
      message += `\n\n*NOTAS*: ${formState.additionalInfo}`;
    }

    return encodeURIComponent(message);
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    const message = generateWhatsAppMessage();
    const whatsappUrl = `https://wa.me/${SITE_CONFIG.whatsapp}?text=${message}`;

    setTimeout(() => {
      window.open(whatsappUrl, '_blank');
      setIsSubmitting(false);
      if (onSuccess) onSuccess();
    }, 500);
  };

  // ==========================================
  // RENDER STEPS
  // ==========================================

  const renderStep1 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-gray-800 text-center mb-4">
        ¿Qué tipo de espacio necesitas limpiar?
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {PROPERTY_OPTIONS.map((option) => {
          const IconComponent = option.icon;
          const isSelected = formState.propertyType === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => handleInputChange('propertyType', option.id)}
              className={`relative p-4 rounded-2xl border-2 transition-all duration-300 text-center group ${
                isSelected
                  ? 'border-transparent shadow-lg scale-105'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
              }`}
            >
              {isSelected && (
                <div className={`absolute inset-0 bg-gradient-to-br ${option.color} opacity-10 rounded-2xl`} />
              )}
              <div className={`w-12 h-12 mx-auto mb-2 rounded-xl flex items-center justify-center transition-all ${
                isSelected
                  ? `bg-gradient-to-br ${option.color} shadow-lg`
                  : 'bg-gray-100 group-hover:bg-gray-200'
              }`}>
                <IconComponent className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-gray-600'}`} />
              </div>
              <div className={`text-sm font-semibold ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                {option.name}
              </div>
              {isSelected && (
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                  <CheckIcon className="w-4 h-4 text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-gray-800 text-center mb-4">
        ¿Cuántos metros cuadrados tiene?
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {SQUARE_METER_OPTIONS.map((option) => {
          const isSelected = formState.squareMeters === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => handleInputChange('squareMeters', option.id)}
              className={`p-4 rounded-2xl border-2 transition-all duration-300 text-left ${
                isSelected
                  ? 'border-cyan-500 bg-gradient-to-br from-cyan-50 to-teal-50 shadow-lg'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md bg-white'
              }`}
            >
              <div className={`text-base font-bold ${isSelected ? 'text-cyan-700' : 'text-gray-800'}`}>
                {option.label}
              </div>
              <div className={`text-xs mt-1 ${isSelected ? 'text-cyan-600' : 'text-gray-500'}`}>
                {option.description}
              </div>
              {isSelected && (
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center shadow-lg">
                  <CheckIcon className="w-4 h-4 text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-5">
      <h3 className="text-lg font-bold text-gray-800 text-center mb-2">
        Detalles del espacio
      </h3>

      {/* Rooms & Bathrooms */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Habitaciones</label>
          <div className="flex gap-2">
            {ROOM_OPTIONS.map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => handleInputChange('rooms', num)}
                className={`flex-1 py-2.5 rounded-xl font-bold text-sm transition-all ${
                  formState.rooms === num
                    ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Baños</label>
          <div className="flex gap-2">
            {BATHROOM_OPTIONS.map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => handleInputChange('bathrooms', num)}
                className={`flex-1 py-2.5 rounded-xl font-bold text-sm transition-all ${
                  formState.bathrooms === num
                    ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Frequency */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Frecuencia del servicio</label>
        <div className="grid grid-cols-2 gap-2">
          {FREQUENCY_OPTIONS.map((option) => {
            const isSelected = formState.frequency === option.id;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => handleInputChange('frequency', option.id)}
                className={`p-3 rounded-xl border-2 transition-all duration-200 ${
                  isSelected
                    ? `border-transparent bg-gradient-to-r ${option.color} text-white shadow-lg`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="font-bold text-sm">{option.label}</div>
                <div className={`text-xs ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>{option.description}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Location */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Ubicación</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {LOCATION_OPTIONS.map((loc) => {
            const isSelected = formState.location === loc;
            return (
              <button
                key={loc}
                type="button"
                onClick={() => handleInputChange('location', loc)}
                className={`p-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isSelected
                    ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                {loc}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => {
    const selectedProperty = PROPERTY_OPTIONS.find(p => p.id === formState.propertyType);
    const selectedSize = SQUARE_METER_OPTIONS.find(s => s.id === formState.squareMeters);
    const selectedFrequency = FREQUENCY_OPTIONS.find(f => f.id === formState.frequency);

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-gray-800 text-center mb-2">
          Confirma tu cotización
        </h3>

        {/* Summary Card */}
        <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl p-4 border border-cyan-200">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-500">Tipo:</span>
              <span className="font-semibold text-gray-800 ml-1">{selectedProperty?.name}</span>
            </div>
            <div>
              <span className="text-gray-500">Tamaño:</span>
              <span className="font-semibold text-gray-800 ml-1">{selectedSize?.label}</span>
            </div>
            <div>
              <span className="text-gray-500">Habitaciones:</span>
              <span className="font-semibold text-gray-800 ml-1">{formState.rooms}</span>
            </div>
            <div>
              <span className="text-gray-500">Baños:</span>
              <span className="font-semibold text-gray-800 ml-1">{formState.bathrooms}</span>
            </div>
            <div>
              <span className="text-gray-500">Frecuencia:</span>
              <span className="font-semibold text-gray-800 ml-1">{selectedFrequency?.label}</span>
            </div>
            <div>
              <span className="text-gray-500">Ubicación:</span>
              <span className="font-semibold text-gray-800 ml-1">{formState.location}</span>
            </div>
          </div>
        </div>

        {/* Optional Fields */}
        <div className="space-y-3">
          <input
            type="text"
            value={formState.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Tu nombre (opcional)"
            className="w-full p-3 rounded-xl border-2 border-gray-200 focus:border-cyan-500 focus:ring-0 transition-colors text-gray-700 placeholder-gray-400"
          />
          <textarea
            value={formState.additionalInfo}
            onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
            placeholder="Notas adicionales (mascotas, áreas específicas, etc.)"
            rows={2}
            className="w-full p-3 rounded-xl border-2 border-gray-200 focus:border-cyan-500 focus:ring-0 transition-colors text-gray-700 placeholder-gray-400 resize-none"
          />
        </div>
      </div>
    );
  };

  // ==========================================
  // MAIN RENDER
  // ==========================================

  return (
    <div className="flex flex-col h-full">
      {/* Progress Steps */}
      <div className="bg-gradient-to-r from-cyan-500 to-teal-500 px-4 py-3">
        <div className="flex items-center justify-between max-w-md mx-auto">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                currentStep >= step.id
                  ? 'bg-white text-cyan-600 shadow-lg'
                  : 'bg-white/30 text-white'
              }`}>
                {currentStep > step.id ? (
                  <CheckIcon className="w-5 h-5" />
                ) : (
                  step.id
                )}
              </div>
              {index < STEPS.length - 1 && (
                <div className={`w-8 sm:w-12 h-1 mx-1 rounded transition-all ${
                  currentStep > step.id ? 'bg-white' : 'bg-white/30'
                }`} />
              )}
            </div>
          ))}
        </div>
        <div className="text-center mt-2 text-white/90 text-sm font-medium">
          {STEPS[currentStep - 1]?.title}
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="border-t border-gray-100 p-4 bg-gray-50">
        <div className="flex gap-3">
          {currentStep > 1 && (
            <button
              onClick={prevStep}
              className="flex-1 py-3 px-4 rounded-full border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition-all flex items-center justify-center gap-2"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Atrás
            </button>
          )}

          {currentStep < 4 ? (
            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className={`flex-1 py-3 px-4 rounded-full font-semibold transition-all flex items-center justify-center gap-2 ${
                canProceed()
                  ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-lg hover:shadow-xl hover:scale-[1.02]'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Siguiente
              <ArrowRightIcon className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 py-3 px-4 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <WhatsAppIcon className="w-5 h-5" />
                  Enviar por WhatsApp
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PriceCalculator;
